import type { ReactNode } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard,
  Briefcase,
  Clock,
  CheckCircle,
  Calendar,
  DollarSign,
  CreditCard,
  FileText,
  FileCheck,
  BadgeCheck,
  User,
  Clock3,
  BarChart3,
  HelpCircle,
  LogOut,
  Bell,
  Settings,
  ChevronDown,
  Menu,
  X
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import NotificationBell from "@/components/NotificationBell";

interface ProviderLayoutProps {
  children: ReactNode;
}

type NavItemProps = {
  icon: ReactNode;
  label: string;
  active?: boolean;
  href?: string;
  section?: string;
};

function NavItem({ icon, label, active, href }: NavItemProps) {
  const location = useLocation();

  const isActive = active || (href && location.pathname.startsWith(href.split('/').slice(0, 3).join('/')));

  const content = (
    <div className={`relative shrink-0 w-full cursor-pointer rounded-r-lg transition-all duration-200 ${isActive ? "bg-accent/5" : "hover:bg-[#f2f4f6] hover:translate-x-1"}`}>
      {isActive && (
        <div aria-hidden="true" className="absolute border-[#00543b] border-l-4 border-solid inset-0 pointer-events-none rounded-r-lg" />
      )}
      <div className="flex flex-row items-center size-full">
        <div className={`content-stretch flex items-center ${isActive ? "pl-[12px] pr-[20px]" : "px-[16px]"} py-[12px] relative w-full gap-[10px]`}>
          <div className={isActive ? "text-[#00543b]" : "text-[#64748b]"}>
            {icon}
          </div>
          <div
            className={`flex flex-col font-medium h-[20px] justify-center leading-[0] not-italic relative shrink-0 text-[14px] tracking-[0.35px] ${isActive ? "text-[#00543b]" : "text-[#64748b]"}`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <p className="leading-[20px]">{label}</p>
          </div>
        </div>
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href} style={{ textDecoration: 'none', display: 'block', width: '100%' }}>
        {content}
      </Link>
    );
  }

  return content;
}

function SideNavBar() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  const navSections = [
    {
      title: null,
      items: [
        { icon: <LayoutDashboard className="w-5 h-5" />, label: "Dashboard", href: "/provider/dashboard" },
        { icon: <Clock className="w-5 h-5" />, label: "Active Jobs", href: "/provider/active-jobs" },
        { icon: <Calendar className="w-5 h-5" />, label: "Schedule", href: "/provider/schedule" },
        { icon: <CheckCircle className="w-5 h-5" />, label: "Completed Jobs", href: "/provider/completed-jobs" },
      ]
    },
    {
      title: "EARNINGS",
      items: [
        { icon: <DollarSign className="w-5 h-5" />, label: "Earnings", href: "/provider/earnings" },
        { icon: <CreditCard className="w-5 h-5" />, label: "Payouts", href: "/provider/payouts" },
        { icon: <FileText className="w-5 h-5" />, label: "Invoices", href: "/provider/invoices" },
      ]
    },
    {
      title: "DOCUMENTS",
      items: [
        { icon: <FileCheck className="w-5 h-5" />, label: "Documents", href: "/provider/documents" },
        { icon: <BadgeCheck className="w-5 h-5" />, label: "Verification Status", href: "/provider/verification" },
        { icon: <FileText className="w-5 h-5" />, label: "Certifications", href: "/provider/certifications" },
      ]
    },
    {
      title: "ACCOUNT",
      items: [
        { icon: <User className="w-5 h-5" />, label: "Profile", href: "/provider/profile" },
      ]
    }
  ];

  return (
    <div className="hidden lg:flex fixed left-0 top-0 bg-white flex-col items-start h-screen w-[288px] z-10 border-r border-gray-200">
      {/* Brand with gradient logo */}
      <div className="relative shrink-0 w-full">
        <div className="content-stretch flex flex-col gap-[8px] items-start px-[32px] py-[40px] relative w-full">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-sm mb-1">
            <span className="text-white font-bold text-lg">V</span>
          </div>
          <div className="flex flex-col font-bold justify-center leading-[0] not-italic relative shrink-0 text-[#00543b] text-[20px] w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
            <p className="leading-[28px]">Virewire</p>
          </div>
          <div className="flex flex-col font-normal justify-center leading-[0] not-italic relative shrink-0 text-[#94a3b8] text-[10px] tracking-[2px] uppercase w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
            <p className="leading-[15px]">Provider Portal</p>
          </div>
        </div>
      </div>

      {/* Nav items - scrollable but with bottom padding */}
      <div className="flex-1 relative w-full overflow-y-auto pb-4">
        <div className="content-stretch flex flex-col gap-[8px] items-start px-[16px] py-[16px] relative w-full">
          {navSections.map((section, sectionIdx) => (
            <div key={sectionIdx} className="w-full">
              {section.title && (
                <div className="px-[16px] py-[12px] text-[10px] font-semibold text-[#94a3b8] tracking-[1px] uppercase w-full">
                  {section.title}
                </div>
              )}
              <div className="flex flex-col gap-[4px]">
                {section.items.map((item) => (
                  <NavItem key={item.label} icon={item.icon} label={item.label} href={item.href} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Logout Button */}
      <div className="relative shrink-0 w-full border-t border-gray-200">
        <div className="content-stretch flex flex-col items-start p-[16px] relative w-full gap-[8px]">
          <button
            onClick={() => {
              localStorage.removeItem('token');
              logout();
              navigate('/provider/login');
            }}
            className="w-full px-[16px] py-[12px] rounded-[8px] hover:bg-[#f2f4f6] transition-colors flex items-center gap-[10px] text-[#64748b]"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-[14px] font-medium">Logout</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function TopNavBar({ onMenuClick }: { onMenuClick: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const pageTitles: Record<string, { title: string; subtitle: string }> = {
    '/provider/dashboard': { title: 'Dashboard', subtitle: 'Overview of your jobs and earnings' },
    '/provider/available-jobs': { title: 'Available Jobs', subtitle: 'Browse and accept new jobs' },
    '/provider/active-jobs': { title: 'Active Jobs', subtitle: 'Jobs currently in progress' },
    '/provider/schedule': { title: 'Schedule', subtitle: 'Your upcoming job schedule' },
    '/provider/completed-jobs': { title: 'Completed Jobs', subtitle: 'History of completed jobs' },
    '/provider/earnings': { title: 'Earnings', subtitle: 'Your income and payment history' },
    '/provider/payouts': { title: 'Payouts', subtitle: 'Payout requests and history' },
    '/provider/invoices': { title: 'Invoices', subtitle: 'Your job invoices' },
    '/provider/documents': { title: 'Documents', subtitle: 'Compliance and certification documents' },
    '/provider/verification': { title: 'Verification Status', subtitle: 'Account verification details' },
    '/provider/certifications': { title: 'Certifications', subtitle: 'Your professional certifications' },
    '/provider/profile': { title: 'Profile', subtitle: 'Manage your account details' },
    '/provider/availability': { title: 'Availability', subtitle: 'Set your working hours' },
    '/provider/performance': { title: 'Performance', subtitle: 'Your performance metrics and reports' },
    '/provider/support': { title: 'Support', subtitle: 'Get help and raise tickets' },
  };

  const current = Object.entries(pageTitles).find(([path]) => location.pathname.startsWith(path));
  const pageInfo = current?.[1] ?? { title: 'Provider Portal', subtitle: 'Manage your jobs and earnings' };
  const userInitial = user?.first_name?.charAt(0).toUpperCase() || 'P';

  return (
    <div className="fixed top-0 left-0 lg:left-[288px] right-0 bg-white border-b border-gray-200 h-[60px] lg:h-[80px] flex items-center justify-between px-4 md:px-8 lg:px-[32px] z-20">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button
          onClick={onMenuClick}
          className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5 text-gray-600" />
        </button>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-foreground text-sm md:text-base truncate">{pageInfo.title}</h2>
          <p className="text-xs text-muted-foreground hidden sm:block">{pageInfo.subtitle}</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        {/* Notification Bell */}
        <NotificationBell />

        <button
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Profile Settings"
          onClick={() => navigate('/provider/profile')}
        >
          <Settings className="w-5 h-5 text-gray-600" />
        </button>

        {/* User Menu Dropdown */}
        <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <div className="flex items-center gap-3 ml-2 pl-4 border-l border-gray-200 cursor-pointer hover:opacity-80 transition">
              <div className="text-right hidden sm:block">
                <p className="text-xs font-medium text-gray-900">{user?.first_name || 'Provider'} {user?.last_name || ''}</p>
                <p className="text-xs text-gray-600">Service Provider</p>
              </div>
              <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {userInitial}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-600 hidden sm:block" />
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground border-b">
              {user?.first_name} {user?.last_name}
            </div>
            <DropdownMenuItem asChild>
              <Link to="/provider/profile" className="cursor-pointer text-sm">
                <User className="h-4 w-4 mr-2" />
                Profile Settings
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive cursor-pointer text-sm"
              onClick={() => {
                logout();
                navigate('/provider/login');
              }}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}

export default function ProviderLayout({ children }: ProviderLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="bg-[#f8f9fb] min-h-screen w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
      {/* Desktop Sidebar */}
      <SideNavBar />

      {/* Mobile overlay */}
      {drawerOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30 transition-opacity duration-200"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed left-0 top-0 h-screen w-[280px] bg-white z-40 shadow-2xl transition-transform duration-300 ${
          drawerOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <span className="font-bold text-lg text-teal-700">Virewire</span>
            <button
              onClick={() => setDrawerOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <SideNavBar />
        </div>
      </div>

      {/* Top Nav */}
      <TopNavBar onMenuClick={() => setDrawerOpen(true)} />

      {/* Main content area */}
      <main className="lg:ml-[288px] pt-[60px] lg:pt-[80px] pb-[32px] px-4 md:px-8 lg:px-[32px]">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
